import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import {
  Phone,
  MessageSquare,
  Shield,
  Tractor,
  Microchip,
  Coins,
  GraduationCap,
  HeartPulse,
  ArrowRight,
  Brain,
  Sprout,
  LineChart,
  BookOpen,
  Headphones,
  ChevronRight,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import '../styles/voxit.css';

const solutions = [
  {
    id: 'omniagent',
    name: 'Voxit OmniAgent',
    subtitle: 'AI Voice & Chat Agent',
    description: 'Secure AI agent that answers phone calls, learns your company knowledge, and takes real actions - without exposing data to public AI.',
    icon: Phone,
    color: 'ai',
    featured: true,
    features: [
      'Voice + Chat + Real actions',
      'Multilingual (10+ languages)',
      'Enterprise security',
      'CRM & ticketing integration',
    ],
    link: '/omniagent',
  },
  {
    id: 'totalfarm-pro',
    name: 'TotalFarm.Pro',
    subtitle: 'Complete Farm Management',
    description: 'All-in-one farm management platform that makes life easier for farmers with AI-powered advice, worker management, and profit tracking.',
    icon: Tractor,
    color: 'agri',
    features: [
      'AI Farm Advisor & Crop Calendar',
      'Worker & Payment Management',
      'Cost Tracking & Budget Planning',
      'Yield Recording & Profit Projection',
    ],
    link: '/totalfarm',
  },
  {
    id: 'smart-farming',
    name: 'TechIQ Smart Farming Box',
    subtitle: 'IoT Soil Analytics',
    description: 'Smart IoT sensor platform for real-time farm monitoring, AI analysis, and automated alerts to optimize farming operations.',
    icon: Microchip,
    color: 'agri',
    features: [
      'Soil moisture & pH monitoring',
      'AI-powered analysis',
      'Real-time alerts',
      'Mobile app access',
    ],
    link: '/techiqbox',
  },
  {
    id: 'fyntquick',
    name: 'FyntQuick Loan',
    subtitle: 'Fintech Platform',
    description: 'Fast and secure digital lending platform with instant approvals, multiple payment options, and 24/7 support.',
    icon: Coins,
    color: 'fintech',
    features: [
      'Instant loan approval',
      'Credit score monitoring',
      'Digital document signing',
      '24/7 customer support',
    ],
    link: '/fyntloan',
  },
  {
    id: 'somo',
    name: 'SOMO Learn',
    subtitle: 'Never Miss a Class',
    description: 'Mobile-first offline learning platform with recorded lessons from real teachers, AI-powered quizzes, and scholarship opportunities.',
    icon: GraduationCap,
    color: 'edu',
    features: [
      'Offline-first design',
      'AI-generated quizzes (GPT-4o)',
      'Points & streaks gamification',
      '1-on-1 expert tutoring',
    ],
    link: '/somolearn',
  },
  {
    id: 'swiftguard',
    name: 'SwiftGuard Responder',
    subtitle: 'Security & Health',
    description: 'Emergency response platform combining security and health monitoring for rapid incident response and management.',
    icon: HeartPulse,
    color: 'health',
    features: [
      'Real-time alerts',
      'GPS tracking',
      'Emergency dispatch',
      'Health monitoring',
    ],
    link: '/swiftguard',
  },
];

const timeline = [
  {
    step: '01',
    title: 'Discovery & Analysis',
    description: 'We start by understanding your business challenges, goals, and requirements through in-depth consultations.',
  },
  {
    step: '02',
    title: 'Strategy & Planning',
    description: 'Our team designs a comprehensive solution architecture and project roadmap tailored to your specific needs.',
  },
  {
    step: '03',
    title: 'Development & Integration',
    description: 'Using agile methodology, we build and integrate solutions with your existing systems seamlessly.',
  },
  {
    step: '04',
    title: 'Testing & Optimization',
    description: 'Rigorous testing and performance optimization ensure your solution meets the highest quality standards.',
  },
  {
    step: '05',
    title: 'Deployment & Support',
    description: 'We handle deployment and provide ongoing support, maintenance, and continuous improvement services.',
  },
];

const industries = [
  { icon: Brain, name: 'AI & ML', desc: 'Intelligent automation', gradient: 'ai' },
  { icon: Sprout, name: 'Agriculture', desc: 'Smart farming', gradient: 'agri' },
  { icon: LineChart, name: 'Fintech', desc: 'Digital finance', gradient: 'fintech' },
  { icon: BookOpen, name: 'Education', desc: 'E-learning platforms', gradient: 'edu' },
  { icon: HeartPulse, name: 'Healthcare', desc: 'Health tech', gradient: 'health' },
  { icon: Headphones, name: 'BPO', desc: 'Support services', gradient: 'bpo' },
];

const HomePage = () => {
  return (
    <div className="voxit-page">
      <Header />
      
      {/* Hero Section */}
      <section className="voxit-hero">
        <div className="container-custom">
          <div className="voxit-hero-content">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="voxit-hero-badge"
            >
              <span className="pulse-dot" />
              AI-Powered Solutions for Tomorrow
            </motion.div>
            
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1, duration: 0.6 }}
            >
              Transforming Industries with <span className="gradient-text">Innovation</span>
            </motion.h1>
            
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="voxit-hero-subtitle"
            >
              From AI voice agents to smart farming solutions, fintech platforms to educational technology - we build the future of business across multiple sectors.
            </motion.p>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.6 }}
              className="voxit-hero-buttons"
            >
              <a href="#solutions" className="btn-voxit-primary">
                Explore Solutions
              </a>
              <a href="#contact" className="btn-voxit-secondary">
                Free Consultation
              </a>
            </motion.div>
          </div>
        </div>
        
        {/* Background Elements */}
        <div className="hero-bg-elements">
          <div className="hero-blob-1" />
          <div className="hero-blob-2" />
          <div className="hero-grid" />
        </div>
      </section>
      
      {/* Solutions Section */}
      <section className="voxit-solutions" id="solutions">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="section-header"
          >
            <span className="section-tag">Our Solutions</span>
            <h2>Innovative Products & Platforms</h2>
            <p>Cutting-edge technology solutions designed to transform how businesses operate across multiple industries</p>
          </motion.div>
          
          <div className="solutions-grid">
            {solutions.map((solution, index) => (
              <motion.div
                key={solution.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
              >
                <div className={`solution-card ${solution.featured ? 'featured' : ''}`}>
                  {solution.featured && (
                    <span className="featured-badge">Featured</span>
                  )}
                  
                  <div className={`solution-header gradient-${solution.color}`}>
                    <div className="solution-icon">
                      <solution.icon size={28} />
                    </div>
                    <h3>{solution.name}</h3>
                    <span className="solution-subtitle">{solution.subtitle}</span>
                  </div>
                  
                  <div className="solution-body">
                    <p>{solution.description}</p>
                    <ul>
                      {solution.features.map((feature, i) => (
                        <li key={i}>
                          <ChevronRight size={14} />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    <Link to={solution.link} className="solution-link">
                      Learn More <ArrowRight size={16} />
                    </Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Timeline Section */}
      <section className="voxit-timeline">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="section-header light"
          >
            <span className="section-tag light">How We Work</span>
            <h2>Our Process</h2>
            <p>From concept to deployment, we follow a proven methodology to deliver exceptional results</p>
          </motion.div>
          
          <div className="timeline-container">
            <div className="timeline-line" />
            {timeline.map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15, duration: 0.5 }}
                className={`timeline-item ${index % 2 === 0 ? 'left' : 'right'}`}
              >
                <div className="timeline-content">
                  <span className="timeline-step">{item.step}</span>
                  <h4>{item.title}</h4>
                  <p>{item.description}</p>
                </div>
                <div className="timeline-dot" style={{ '--dot-index': index }} />
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Industries Section */}
      <section className="voxit-industries">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="section-header"
          >
            <span className="section-tag">Industries We Serve</span>
            <h2>Multi-Sector Expertise</h2>
            <p>Delivering specialized solutions across diverse industries worldwide</p>
          </motion.div>
          
          <div className="industries-grid">
            {industries.map((industry, index) => (
              <motion.div
                key={industry.name}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.4 }}
                className="industry-box"
              >
                <div className={`industry-icon gradient-${industry.gradient}`}>
                  <industry.icon size={32} />
                </div>
                <h4>{industry.name}</h4>
                <p>{industry.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Stats Section */}
      <section className="voxit-stats">
        <div className="container-custom">
          <div className="stats-grid">
            {[
              { number: '50+', label: 'Projects Delivered' },
              { number: '6', label: 'Industry Sectors' },
              { number: '10+', label: 'Countries Served' },
              { number: '24/7', label: 'Support Available' },
            ].map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                className="stat-item"
              >
                <span className="stat-number">{stat.number}</span>
                <span className="stat-label">{stat.label}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="voxit-cta" id="contact">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="cta-content"
          >
            <h2>Ready to Transform Your Business?</h2>
            <p>Let's discuss how our solutions can help you achieve your goals. Get a free consultation today.</p>
            <div className="cta-buttons">
              <a href="mailto:sales@voxitconsulting.media?subject=Schedule a Call - VoxIT Inquiry" className="btn-voxit-primary">
                Schedule a Call
              </a>
              <Link to="/omniagent" className="btn-voxit-secondary">
                Explore Voxit OmniAgent
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default HomePage;
